/**
 * Formatting only. No money arithmetic happens on the client.
 *
 * Every figure the client shows was computed and rounded by the Worker and
 * stored — so a printed figure is exactly the stored figure, and a column total
 * is exactly the sum of the printed rows. The prototype rounds at display while
 * summing unrounded floats, so its totals do not match its own rows.
 *
 * If you find yourself dividing or multiplying money here, it belongs in
 * api/src/domain/pay.ts instead.
 */

/** `Rs 1,234`. The only money format, on screen and on paper. */
export function formatRs(rupees: number): string {
  return `Rs ${Math.trunc(rupees).toLocaleString('en-US')}`;
}

/** Hours are stored as tenths: 80 → `8`, 65 → `6.5`. */
export function formatHours(tenths: number): string {
  const hours = tenths / 10;
  return Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
}

/** Litres are stored as hundredths. */
export function formatLitres(litresX100: number): string {
  const litres = litresX100 / 100;
  return Number.isInteger(litres) ? String(litres) : litres.toFixed(2);
}

/** `Mon, 21 Sep 2026` — the prototype's dstr(). */
export function formatDate(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

/** `21 Sep` — the short form used in banners and table cells. */
export function formatDateShort(date: string): string {
  return new Date(`${date}T00:00:00`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });
}

/** Amount in words for an advance receipt: "four thousand two hundred". */
export function rupeesInWords(amount: number): string {
  let n = Math.trunc(amount);
  if (n <= 0) return 'zero';

  const ones = [
    '', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
    'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen',
    'seventeen', 'eighteen', 'nineteen',
  ];
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

  const under1000 = (x: number): string => {
    if (x < 20) return ones[x]!;
    if (x < 100) return tens[Math.floor(x / 10)]! + (x % 10 ? `-${ones[x % 10]!}` : '');
    return `${ones[Math.floor(x / 100)]!} hundred${x % 100 ? ` ${under1000(x % 100)}` : ''}`;
  };

  let out = '';
  if (n >= 100_000) {
    out += `${under1000(Math.floor(n / 100_000))} lakh `;
    n %= 100_000;
  }
  if (n >= 1000) {
    out += `${under1000(Math.floor(n / 1000))} thousand `;
    n %= 1000;
  }
  if (n) out += under1000(n);
  return out.trim();
}
